
import React, { useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { Order, CardapioWebConfig } from '../types';
import { pollCardapioOrders, getCardapioOrderDetails, readyCardapioOrder } from '../services/cardapioWebService';
import { mapCardapioToInternal } from '../services/cardapioWebMapper';
import { playSound } from '../services/audioService';
import { shouldMaskGhostDispatchExternalStatus } from '../services/ghostDispatchPolicy';
import {
    resolveSyncedOrderStatus,
    resolveSyncedPickupConfirmed,
    shouldClearDriverLinkForStatus
} from '../services/orderStatusSyncPolicy';
import { supabase } from '../services/supabase';

const MAX_DETAILS_CONCURRENCY = 5;

const mapWithConcurrency = async <T, R>(
    items: T[],
    concurrency: number,
    worker: (item: T) => Promise<R>
): Promise<R[]> => {
    if (items.length === 0) return [];

    const results: R[] = new Array(items.length);
    let nextIndex = 0;

    const runWorker = async () => {
        while (true) {
            const currentIndex = nextIndex;
            nextIndex += 1;
            if (currentIndex >= items.length) return;
            results[currentIndex] = await worker(items[currentIndex]);
        }
    };

    const workers = Array.from(
        { length: Math.max(1, Math.min(concurrency, items.length)) },
        () => runWorker()
    );

    await Promise.all(workers);
    return results;
};

const getStoredPollTime = (storeId: string) => {
    try {
        const stored = localStorage.getItem(`sal_last_poll_${storeId}`);
        if (stored) return parseInt(stored);
    } catch { }
    return Date.now() - 4 * 60 * 60 * 1000;
};

const setStoredPollTime = (storeId: string, time: number) => {
    try { localStorage.setItem(`sal_last_poll_${storeId}`, time.toString()); } catch { }
};

const FINAL_CARDAPIO_STATUSES = new Set(['closed', 'delivered', 'canceled', 'canceling']);
const FINAL_INTERNAL_STATUSES = new Set<Order['status']>(['delivered', 'archived', 'canceled']);

const buildFallbackCardapioPayload = (raw: any, rawId: string, fallbackStatus: string, existing?: Order) => {
    const nowIso = new Date().toISOString();
    return {
        id: rawId,
        status: fallbackStatus,
        order_type: raw?.order_type || (existing?.type === 'TAKEOUT' ? 'takeout' : 'delivery'),
        created_at: raw?.created_at || nowIso,
        updated_at: raw?.updated_at || raw?.created_at || nowIso,
        customer: raw?.customer || { name: existing?.customerName || 'Cliente (Sincronizacao)' },
        items: raw?.items || [{ quantity: 1, name: 'Pedido em sincronizacao' }],
        total: raw?.total ?? existing?.orderValue ?? 0,
        delivery_fee: raw?.delivery_fee ?? existing?.deliveryFee ?? 0,
        payments: raw?.payments || []
    };
};

/**
 * v44.7: Maestro de Sincronia - SMART GHOST DISPATCH ENGINE
 * - Implementa Modo Dinâmico (Anti-Robô) e Modo Preditivo (JIT).
 * - REGRAS DE OURO: Apenas iFood + Delivery, Reconciliação de Status Fantasma.
 */
export const useSalProcesses = (data: any, ui: any, intelligence: any, actions: any) => {
    const { orders, setOrders, config, storeId } = data;

    const ordersRef = useRef<Order[]>(orders);
    const configRef = useRef(config);
    const notifiedOrderIds = useRef<Set<string>>(new Set());

    const lastPollTimeRef = useRef<number>(storeId ? getStoredPollTime(storeId) : Date.now() - 4 * 60 * 60 * 1000);
    const isPollingRef = useRef(false);
    const emergencySyncRef = useRef<(() => Promise<void>) | null>(null);
    const lastRealtimeRecoveryAtRef = useRef(0);

    useEffect(() => { ordersRef.current = orders; }, [orders]);
    useEffect(() => { configRef.current = config; }, [config]);

    const processOrderSync = useCallback((incoming: Order) => {
        setOrders((prev: Order[]) => {
            const nextState = [...prev];
            const pId = String(incoming.platformId);
            const idx = nextState.findIndex(o => String(o.platformId) === pId);

            if (idx === -1) {
                if (!['archived', 'canceled', 'delivered'].includes(incoming.status)) {
                    nextState.push(incoming);
                    if (!notifiedOrderIds.current.has(pId)) {
                        notifiedOrderIds.current.add(pId);
                        // v56.8: Não notificar sonora/visualmente pedidos presuntivos (ex: aguardando pagamento)
                        // Eles aparecem na lista mas não incomodam o operador até confirmarem.
                        if (incoming.status !== 'payment_pending') {
                            toast.success(`Novo Pedido: ${incoming.displayId || incoming.id}`, { id: `new_${pId}` });
                            playSound('success');
                        }
                    }
                }
            } else {
                const current = nextState[idx];

                if (FINAL_INTERNAL_STATUSES.has(current.status) && !FINAL_INTERNAL_STATUSES.has(incoming.status)) {
                    return nextState;
                }

                if (incoming.status === 'canceled' && current.status !== 'canceled') {
                    toast.error(`Pedido ${current.displayId || current.id} CANCELADO no PDV`, { id: `cnc_${pId}` });
                    nextState[idx] = { ...current, status: 'canceled', statusUpdatedAt: Date.now() };
                    return nextState;
                }

                // Regra de Ouro: despacho fantasma afeta apenas PDV externo, nunca status logistico interno
                const shouldMaskGhostDispatch = shouldMaskGhostDispatchExternalStatus(current, incoming.status);
                const incomingStatus = shouldMaskGhostDispatch ? current.status : incoming.status;

                const finalStatus = resolveSyncedOrderStatus(
                    { status: current.status, driverId: current.driverId },
                    incomingStatus
                );

                // v56.10: Também protegemos isReady se o status local for 'ready'
                // Isso evita que o spread de incoming sobrescreva isReady=true com isReady=false do servidor
                const finalIsReady = shouldMaskGhostDispatch
                    ? current.isReady
                    : ((finalStatus === 'ready') ? true : (incoming.isReady ?? current.isReady));
                const hasEffectiveStatusChange = current.status !== finalStatus;
                const shouldClearDriverLink = shouldClearDriverLinkForStatus(finalStatus);
                const finalPickupConfirmed = resolveSyncedPickupConfirmed(current, incoming, finalStatus);

                nextState[idx] = {
                    ...current,
                    ...incoming,
                    id: current.id,
                    driverId: shouldClearDriverLink ? undefined : current.driverId,
                    ghostDispatch: current.ghostDispatch || incoming.ghostDispatch,
                    manualAssignment: current.manualAssignment,
                    smartClusterId: current.smartClusterId,
                    routeSequence: current.routeSequence,
                    incidents: current.incidents,
                    pickupConfirmed: finalPickupConfirmed,
                    status: finalStatus,
                    isReady: finalIsReady, // v56.10: Proteção explícita
                    statusUpdatedAt: hasEffectiveStatusChange ? Date.now() : (incoming.statusUpdatedAt || current.statusUpdatedAt)
                };

                if (hasEffectiveStatusChange) {
                    toast(`Pedido ${current.displayId || current.id}: ${finalStatus.toUpperCase()}`, { id: `upd_${pId}`, icon: 'ℹ️' });
                }
            }
            return nextState;
        });
    }, [setOrders]);

    // 1. LISTENER REALTIME (WEBHOOK)
    useEffect(() => {
        const active = config.cardapioWeb?.active;
        if (!storeId || !active) return;

        const channel = supabase
            .channel(`sal-cardapio-buffer-${storeId}`)
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'sal_cardapioweb_buffer', filter: `store_id=eq.${storeId}` },
                async (payload) => {
                    const row: any = payload.new || payload.old;
                    if (!row || row.processed) return;
                    const raw = row.payload || row;
                    const rawId = String(raw.id || raw.order_id || row.order_id);
                    const rawStatus = String(raw.status || raw.order_status || '').trim();

                    const currentConfig = configRef.current;
                    const hubConfig = { 
                        lat: currentConfig.businessLat, 
                        lng: currentConfig.businessLng, 
                        googleMapsApiKey: currentConfig.googleMapsApiKey, 
                        hereMapsApiKey: currentConfig.hereMapsApiKey,
                        storeId 
                    };
                    const markBufferAsProcessed = async (orderId: string) => {
                        await supabase
                            .from('sal_cardapioweb_buffer')
                            .update({ processed: true, processed_at: new Date().toISOString() })
                            .eq('store_id', storeId)
                            .eq('order_id', orderId);
                    };

                    try {
                        let fullData = raw;
                        const existing = ordersRef.current.find(o => String(o.platformId) === rawId);
                        const isIncomplete = !raw.items || raw.items.length === 0 || !raw.customer || raw.total === undefined;
                        if (isIncomplete) {
                            try {
                                const fetched = await getCardapioOrderDetails(currentConfig.cardapioWeb, rawId);
                                if (fetched) fullData = { ...raw, ...fetched };
                                else {
                                    if (!rawStatus) {
                                        return;
                                    }

                                    if (!existing && FINAL_CARDAPIO_STATUSES.has(rawStatus)) {
                                        await markBufferAsProcessed(rawId);
                                        return;
                                    }

                                    console.warn(`[SAL Sync] Detalhes indisponiveis para #${rawId}, aplicando fallback de evento.`);
                                    fullData = buildFallbackCardapioPayload(raw, rawId, rawStatus, existing);
                                }
                            } catch (err) {
                                console.error(`[SAL Sync] Erro grave ao buscar detalhes #${rawId}`, err);
                                return;
                            }
                        }

                        if (rawStatus) {
                            fullData = { ...fullData, status: rawStatus };
                        }

                        const mapped = await mapCardapioToInternal(fullData, hubConfig, existing ? { lat: existing.lat, lng: existing.lng } : undefined);
                        processOrderSync(mapped);

                        await markBufferAsProcessed(String(row.order_id || rawId));

                    } catch (e) {
                        console.error("Erro processamento buffer:", e);
                    }
                }
            )
            .subscribe((status) => {
                if (!['CHANNEL_ERROR', 'TIMED_OUT', 'CLOSED'].includes(status)) return;
                if (isPollingRef.current || !emergencySyncRef.current) return;

                const now = Date.now();
                if (now - lastRealtimeRecoveryAtRef.current < 10000) return;

                lastRealtimeRecoveryAtRef.current = now;
                void emergencySyncRef.current();
            });

        return () => {
            supabase.removeChannel(channel);
        };
    }, [storeId, config.cardapioWeb?.active, processOrderSync]);

    // 2. POLLING DE SEGURANÇA (POLLING ADAPTATIVO)
    useEffect(() => {
        const active = config.cardapioWeb?.active;
        if (!storeId || !active) return;

        const isStoreOpen = () => {
            const hours = configRef.current.operatingHours;
            if (!hours) return true; // Se não configurado, assume aberto

            const now = new Date();
            const day = now.getDay();
            const todayHours = hours[day];
            if (!todayHours || todayHours.length === 0) return false;

            const currentTime = now.getHours() * 60 + now.getMinutes();
            return todayHours.some((range: { start: string; end: string }) => {
                const [hStart, mStart] = range.start.split(':').map(Number);
                const [hEnd, mEnd] = range.end.split(':').map(Number);
                const startMins = hStart * 60 + mStart;
                const endMins = hEnd * 60 + mEnd;
                return currentTime >= startMins && currentTime <= endMins;
            });
        };

        const syncCycle = async () => {
            if (isPollingRef.current) return;

            const open = isStoreOpen();
            const hasActiveOrders = ordersRef.current.some(o => ['pending', 'confirmed', 'ready', 'assigned', 'dispatched'].includes(o.status));

            // Se a loja está fechada E não há pedidos ativos, podemos pular este ciclo se foi há pouco tempo
            // O intervalo do setInterval já cuidará disso de forma adaptativa se mudarmos para um timer dinâmico,
            // mas aqui vamos apenas simplificar: se fechado e sem pedidos, 20% de chance de rodar (reduz 5x o tráfego)
            if (!open && !hasActiveOrders && Math.random() > 0.2) return;

            isPollingRef.current = true;
            const currentConfig = configRef.current;
            const hubConfig = { lat: currentConfig.businessLat, lng: currentConfig.businessLng, googleMapsApiKey: currentConfig.googleMapsApiKey, hereMapsApiKey: currentConfig.hereMapsApiKey, storeId };

            try {
                const pollStart = Date.now();
                const pdvRaw = await pollCardapioOrders(currentConfig.cardapioWeb, lastPollTimeRef.current, true);
                lastPollTimeRef.current = pollStart;
                setStoredPollTime(storeId, pollStart);

                if (pdvRaw.length > 0) {
                    const updatesNeeded = pdvRaw.filter((lite) => {
                        const pId = String(lite.id);
                        const existing = ordersRef.current.find(o => String(o.platformId) === pId);
                        const liteUpdatedAt = lite.updated_at ? new Date(lite.updated_at).getTime() : 0;
                        const existingUpdatedAt = existing?.statusUpdatedAt || 0;
                        return !existing || liteUpdatedAt > existingUpdatedAt || lite.status !== existing?.status;
                    });

                    const mappedUpdates = await mapWithConcurrency(
                        updatesNeeded,
                        MAX_DETAILS_CONCURRENCY,
                        async (lite) => {
                            const pId = String(lite.id);
                            const existing = ordersRef.current.find(o => String(o.platformId) === pId);
                            const full = await getCardapioOrderDetails(currentConfig.cardapioWeb, pId);
                            if (full) {
                                return mapCardapioToInternal(
                                    { ...full, status: lite.status || full.status },
                                    hubConfig,
                                    existing ? { lat: existing.lat, lng: existing.lng } : undefined
                                );
                            }
                            return null;
                        }
                    );

                    mappedUpdates.forEach((mapped) => {
                        if (mapped) processOrderSync(mapped);
                    });
                }
            } catch (e) { } finally { isPollingRef.current = false; }
        };

        emergencySyncRef.current = syncCycle;

        const pollingSeconds = Math.max(15, Number(config.cardapioWeb?.pollingIntervalSeconds || 45));
        const interval = setInterval(syncCycle, pollingSeconds * 1000);
        syncCycle();
        return () => {
            emergencySyncRef.current = null;
            clearInterval(interval);
        };
    }, [storeId, config.cardapioWeb?.active, config.cardapioWeb?.pollingIntervalSeconds, processOrderSync]);

    // 3. SMART GHOST DISPATCH MONITOR
    useEffect(() => {
        const active = config.cardapioWeb?.active && config.cardapioWeb?.enableGhostDispatch;
        if (!storeId || !active) return;

        const checkGhostDispatch = async () => {
            const cwConfig = configRef.current.cardapioWeb as CardapioWebConfig;
            const currentOrders = ordersRef.current;
            const now = Date.now();

            // Filtro: Apenas iFood + Delivery + Status Confirmado/Pronto + Sem Despacho Fantasma enviado
            // CRITICAL: Apenas pedidos de FROTA PRÓPRIA EXPLICITAMENTE confirmada são elegíveis
            // Pedidos ainda não processados (optimizationOutcome/manualAssignment undefined) NÃO são elegíveis
            const candidates = currentOrders.filter(o =>
                o.type === 'DELIVERY' &&
                o.salesChannel?.toLowerCase().includes('ifood') &&
                ['confirmed', 'ready'].includes(o.status) &&
                !o.ghostDispatchSent &&
                // Apenas frota própria EXPLICITAMENTE confirmada:
                (o.optimizationOutcome === 'own' || o.manualAssignment === 'own') &&
                // Excluir explicitamente envios externos:
                o.optimizationOutcome !== 'third' &&
                o.manualAssignment !== 'third' &&
                !o.bestProvider &&
                !o.externalDeliveryId
            );

            const updates: Order[] = [];

            for (const order of candidates) {
                let triggerTime = order.ghostDispatchTriggerTime;

                // Calcula o tempo de gatilho se ainda não existir
                if (!triggerTime) {
                    if (cwConfig.ghostDispatchMode === 'dynamic') {
                        const min = cwConfig.ghostDispatchDynamicMinSeconds || 300;
                        const max = cwConfig.ghostDispatchDynamicMaxSeconds || 720;
                        const randomDelay = Math.floor(Math.random() * (max - min + 1) + min) * 1000;
                        triggerTime = order.createdAt + randomDelay;
                    } else if (cwConfig.ghostDispatchMode === 'predictive') {
                        // Modo Preditivo: SLA plataforma - buffer
                        const buffer = (cwConfig.ghostDispatchBufferMinutes || 5) * 60 * 1000;
                        const kitchenTime = (configRef.current.averageKitchenTime || 20) * 60 * 1000;
                        triggerTime = order.createdAt + kitchenTime - buffer;
                    }

                    if (triggerTime) {
                        updates.push({ ...order, ghostDispatchTriggerTime: triggerTime });
                    }
                }

                // Dispara se chegou a hora
                if (triggerTime && now >= triggerTime) {
                    try {
                        const success = await readyCardapioOrder(cwConfig, order.platformId || order.id);
                        if (success) {
                            updates.push({
                                ...order,
                                ghostDispatch: true,
                                ghostDispatchSent: true,
                                ghostDispatchTriggerTime: triggerTime
                            });
                        }
                    } catch (e) {
                        console.error(`[Ghost Dispatch Error] #${order.id}:`, e);
                    }
                }
            }

            if (updates.length > 0) {
                setOrders((prev: Order[]) => {
                    const next = [...prev];
                    updates.forEach(upd => {
                        const idx = next.findIndex(o => o.id === upd.id);
                        if (idx !== -1) next[idx] = { ...next[idx], ...upd };
                    });
                    return next;
                });
            }
        };

        const ghostInterval = setInterval(checkGhostDispatch, 15000); // Checa a cada 15s
        return () => clearInterval(ghostInterval);
    }, [storeId, config.cardapioWeb?.active, config.cardapioWeb?.enableGhostDispatch, setOrders]);

    // 4. OPEN DELIVERY IDENTITY GENERATION (Auto-Self-Sovereign Identity)
    const isGeneratingIdentityRef = useRef(false);
    const hasIdentity = Boolean(config?.openDeliveryIdentity);
    
    useEffect(() => {
        if (!storeId || !config) return;
        
        // Se ainda não tem identidade gerada, gera agora e salva
        if (!hasIdentity && !isGeneratingIdentityRef.current) {
            isGeneratingIdentityRef.current = true;
            
            const newIdentity = {
                merchantId: crypto.randomUUID(),
                merchantSecret: Array.from(crypto.getRandomValues(new Uint8Array(32)))
                    .map(b => b.toString(16).padStart(2, '0')).join('')
            };

            // Usar actions.setConfig se disponível
            if (actions && actions.setConfig) {
                actions.setConfig({ ...config, openDeliveryIdentity: newIdentity })
                    .then(() => {
                        // Identity saved successfully
                    })
                    .catch((err: any) => {
                        console.error("[Open Delivery] Erro ao salvar identidade:", err);
                        isGeneratingIdentityRef.current = false;
                    });
            } else {
                console.warn("[Open Delivery] actions.setConfig não disponível");
                isGeneratingIdentityRef.current = false;
            }
        }
    }, [storeId, hasIdentity, actions, config]);

    // 4. PERSISTENT PENDING ALERT LOOP (2s interval - toca até confirmar/cancelar)
    const pendingAlertOrderIdsRef = useRef<Set<string>>(new Set());
    const alertIntervalRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const currentPendingIds = new Set<string>(orders.filter(o => o.status === 'pending').map(o => o.id));
        
        // Remove IDs que não são mais pending
        pendingAlertOrderIdsRef.current.forEach(id => {
            if (!currentPendingIds.has(id)) {
                pendingAlertOrderIdsRef.current.delete(id);
            }
        });

        // Adiciona novos pedidos pending
        currentPendingIds.forEach(id => {
            pendingAlertOrderIdsRef.current.add(id);
        });

        // Se não há mais pedidos em alerta, para o intervalo
        if (pendingAlertOrderIdsRef.current.size === 0) {
            if (alertIntervalRef.current) {
                clearInterval(alertIntervalRef.current);
                alertIntervalRef.current = null;
            }
            return;
        }

        // Se já existe um intervalo rodando, não cria outro
        if (alertIntervalRef.current) {
            return;
        }

        // Toca o primeiro alerta imediatamente
        playSound('alert');

        // Inicia o intervalo de 2 segundos
        alertIntervalRef.current = setInterval(() => {
            // Re-checa se ainda há pedidos em estado de alerta (pending)
            const stillPending = orders.some(o => 
                pendingAlertOrderIdsRef.current.has(o.id) && o.status === 'pending'
            );
            
            if (stillPending) {
                playSound('alert');
            } else {
                // Não há mais pedidos pendentes em alerta, para o intervalo
                if (alertIntervalRef.current) {
                    clearInterval(alertIntervalRef.current);
                    alertIntervalRef.current = null;
                }
                pendingAlertOrderIdsRef.current.clear();
            }
        }, 2000);

        return () => {
            if (alertIntervalRef.current) {
                clearInterval(alertIntervalRef.current);
                alertIntervalRef.current = null;
            }
        };
    }, [orders]);
};
